<!--
 * @Author: Christion.Young
 * @Date: 2025-07-30 10:00:00
 * @LastEditors: Christion.Young
 * @LastEditTime: 2025-07-30 10:00:00
 * @Description: 应用主组件 - 电商展示平台主布局
 * @Features:
 *   - 响应式布局设计
 *   - 集成头部导航、弹窗组件
 *   - 全局样式管理
 *   - 移动端适配
-->

<template>
  <div id="app">
    <!-- 顶部导航栏 -->

    <Header />

    <!-- 主内容区域 -->

    <main class="main-content">
      <router-view />
    </main>

    <!-- 底部信息栏 -->

    <FooterBar />

    <!-- 全局弹窗组件 -->

    <LoginDialog />

    <ChatDialog />

    <!-- 强制密码修改弹窗 -->
    <el-dialog
      title="安全提示"
      :visible.sync="forcePasswordChangeDialog"
      width="500px"
      :close-on-click-modal="false"
      :show-close="false"
      custom-class="force-password-change-dialog"
    >
      <div class="password-change-header user-id-notice">
        <i class="el-icon-warning"></i>
        <p class="password-change-title">
          请设置新的登录密码，密码长度为6-20位。请在"三火私服"小程序中查看并填写您的用户ID进行身份验证。
        </p>
      </div>

      <el-form
        ref="passwordChangeForm"
        :model="passwordChangeForm"
        :rules="passwordChangeRules"
        label-width="80px"
        class="password-change-form"
      >
        <el-form-item label="当前密码" prop="oldPassword">
          <el-input
            v-model="passwordChangeForm.oldPassword"
            type="password"
            show-password
            placeholder="请输入当前密码"
          />
        </el-form-item>

        <el-form-item label="新密码" prop="newPassword">
          <el-input
            v-model="passwordChangeForm.newPassword"
            type="password"
            show-password
            placeholder="请输入新密码"
          />
        </el-form-item>

        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input
            v-model="passwordChangeForm.confirmPassword"
            type="password"
            show-password
            placeholder="请确认新密码"
            @keyup.enter.native="handleForcePasswordChange"
          />
        </el-form-item>
        <el-form-item label="用户ID" prop="userId">
          <el-input
            v-model="passwordChangeForm.userId"
            type="text"
            placeholder="请输入您的用户ID进行身份验证"
          />
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          @click="handleForcePasswordChange"
          :loading="passwordChangeLoading"
          class="password-change-btn"
        >
          立即修改
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
// 导入布局组件
import Header from "./components/Layout/Header.vue";
import FooterBar from "./components/FooterBar.vue";

// 导入弹窗组件
import LoginDialog from "./components/Common/LoginDialog.vue";
import ChatDialog from "./components/Common/ChatDialog.vue";

export default {
  name: "App",
  components: {
    Header,
    FooterBar,
    LoginDialog,
    ChatDialog,
  },
  data() {
    return {
      // 组件初始化状态
      isInitialized: false,
      // 窗口大小变化防抖定时器
      resizeTimer: null,
      // 强制密码修改弹窗状态
      forcePasswordChangeDialog: false,
      // 密码修改加载状态
      passwordChangeLoading: false,
      // 密码修改表单
      passwordChangeForm: {
        userId: "",
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      },
      // 密码修改表单验证规则
      passwordChangeRules: {
        userId: [
          { required: true, message: "请输入用户ID", trigger: "blur" },
          { min: 1, message: "用户ID不能为空", trigger: "blur" },
        ],
        oldPassword: [
          { required: true, message: "请输入当前密码", trigger: "blur" },
        ],
        newPassword: [
          { required: true, message: "请输入新密码", trigger: "blur" },
          { min: 6, message: "密码长度不能少于6位", trigger: "blur" },
          { validator: this.validateNewPassword, trigger: "blur" },
        ],
        confirmPassword: [
          { required: true, message: "请确认新密码", trigger: "blur" },
          { validator: this.validateConfirmPassword, trigger: "blur" },
        ],
      },
    };
  },
  computed: {},
  created() {
    // 初始化应用
    this.initializeApp();
    // 只有已登录时才检查强制修改密码
    if (this.$store.getters["user/isLoggedIn"]) {
      this.$store.dispatch(
        "user/checkForcePasswordChange",
        this.$store.state.user.userInfo
      );
    }
  },
  mounted() {
    // 添加窗口大小变化监听
    this.addEventListeners();
    // 预加载资源
    this.preloadAssets();
    // 添加强制密码修改事件监听
    this.addForcePasswordChangeListener();
  },
  beforeDestroy() {
    // 移除事件监听器
    this.removeEventListeners();
  },
  methods: {
    /**
     * 初始化应用程序
     */
    initializeApp() {
      // 设置初始化完成标志
      this.isInitialized = true;
    },

    /**
     * 添加事件监听器
     */
    addEventListeners() {
      // 监听网络状态变化
      window.addEventListener("online", this.handleOnline);
      window.addEventListener("offline", this.handleOffline);
    },

    /**
     * 移除事件监听器
     */
    removeEventListeners() {
      window.removeEventListener("online", this.handleOnline);
      window.removeEventListener("offline", this.handleOffline);
      window.removeEventListener(
        "forcePasswordChange",
        this.handleForcePasswordChangeEvent
      );
    },

    /**
     * 处理网络连接恢复
     */
    handleOnline() {
      this.$msg.success("网络连接已恢复");
    },

    /**
     * 处理网络连接断开
     */
    handleOffline() {
      this.$msg.warning("网络连接已断开，请检查网络设置");
    },

    /**
     * 预加载静态资源
     */
    preloadAssets() {
      // 预加载重要图片资源
      const importantImages = [require("./assets/logo.png")];

      // 创建图片对象进行预加载
      importantImages.forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    },

    /**
     * 添加强制密码修改事件监听
     */
    addForcePasswordChangeListener() {
      // 监听自定义事件
      window.addEventListener(
        "forcePasswordChange",
        this.handleForcePasswordChangeEvent
      );

      // 也可以通过 Vuex 状态监听
      this.$watch(
        () => this.$store.getters["user/forcePasswordChange"],
        (newValue) => {
          // 只有已登录时才弹窗
          if (newValue && this.$store.getters["user/isLoggedIn"]) {
            this.showForcePasswordChangeDialog();
          }
        }
      );
    },

    /**
     * 处理强制密码修改事件
     */
    handleForcePasswordChangeEvent(event) {
      console.log("收到强制密码修改事件:", event);
      this.showForcePasswordChangeDialog();
    },

    /**
     * 显示强制密码修改弹窗
     */
    showForcePasswordChangeDialog() {
      // 只有已登录时才显示弹窗
      if (this.$store.getters["user/isLoggedIn"]) {
        console.log("显示强制密码修改弹窗");
        this.forcePasswordChangeDialog = true;
      }
    },

    /**
     * 验证确认密码
     */
    validateConfirmPassword(rule, value, callback) {
      if (value !== this.passwordChangeForm.newPassword) {
        callback(new Error("两次输入的密码不一致"));
      } else {
        callback();
      }
    },

    /**
     * 验证新密码（不能与手机号相同）
     */
    validateNewPassword(rule, value, callback) {
      const currentUserInfo = this.$store.getters["user/userInfo"];
      const userPhone = currentUserInfo?.phone || currentUserInfo?.mobile;

      if (value && userPhone && value === userPhone) {
        callback(new Error("新密码不能与手机号相同"));
      } else {
        callback();
      }
    },

    /**
     * 处理强制密码修改
     */
    async handleForcePasswordChange() {
      try {
        // 验证表单
        await this.$refs.passwordChangeForm.validate();

        // 验证用户ID是否匹配
        const inputUserId = this.passwordChangeForm.userId.trim();
        const currentUserInfo = this.$store.getters["user/userInfo"];
        const currentUserId = currentUserInfo.userId || currentUserInfo.id;

        if (inputUserId !== currentUserId) {
          this.$msg.error("用户ID不匹配，请输入正确的用户ID");
          return;
        }

        this.passwordChangeLoading = true;

        // 调用密码修改API
        const { userAPI } = await import("@/api");

        const userUpdateData = {
          userId: currentUserInfo.userId,
          username: currentUserInfo.username,
          nickname: currentUserInfo.nickname,
          email: "已修改", // 标记已修改密码
          password: this.passwordChangeForm.newPassword,
        };

        console.log("强制密码修改API调用，发送数据:", userUpdateData);

        const response = await userAPI.putObj(userUpdateData);

        if (response && response.code === 0) {
          this.$msg.success("密码修改成功，请重新登录");

          // 重置表单
          this.resetForcePasswordChangeForm();
          this.forcePasswordChangeDialog = false;

          // 清除强制修改状态
          this.$store.commit("user/SET_FORCE_PASSWORD_CHANGE", false);

          // 重新获取用户信息并更新 state.userInfo
          import("@/api").then(({ authAPI }) => {
            authAPI
              .getUserInfo()
              .then((userInfoResponse) => {
                if (userInfoResponse && userInfoResponse.data) {
                  this.$store.commit(
                    "user/SET_USER_INFO",
                    userInfoResponse.data
                  );
                }
              })
              .finally(() => {
                // 延迟后退出登录
                setTimeout(() => {
                  this.$store.dispatch("user/logout");
                  this.$router.push("/");
                }, 1500);
              });
          });
        } else {
          this.$msg.error(response?.msg || "密码修改失败");
        }
      } catch (error) {
        if (error.message) {
          console.log("表单验证失败");
        } else {
          console.error("密码修改失败:", error);
          this.$msg.error("密码修改失败，请稍后重试");
        }
      } finally {
        this.passwordChangeLoading = false;
      }
    },

    /**
     * 重置强制密码修改表单
     */
    resetForcePasswordChangeForm() {
      this.passwordChangeForm = {
        userId: "",
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      };
      this.$refs.passwordChangeForm?.resetFields();
    },
  },
};
</script>

<style lang="scss">
/* 导入全局变量 */
@import "./style/index.scss";

/* 全局样式重置 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html,
body {
  height: 100%;
  font-family: "PingFang SC", "Helvetica Neue", Helvetica, Arial,
    "Microsoft YaHei", sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: #f8f9fa;
}

/* 应用主容器 */
#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
}

/* 主内容区域 */
.main-content {
  flex: 1;
  margin-top: 64px; // 为固定头部留出空间
  min-height: calc(100vh - 64px - 120px); // 减去头部和底部高度
  background: #fff;
}

/* 图片和媒体元素响应式 */
img,
video {
  max-width: 100%;
  height: auto;
  display: block;
}

/* 滚动条美化 */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;

  &:hover {
    background: #a8a8a8;
  }
}

/* Element UI 全局样式覆盖 */
.el-button--primary {
  background-color: $subject-color;
  border-color: $subject-color;

  &:hover {
    background-color: lighten($subject-color, 10%);
    border-color: lighten($subject-color, 10%);
  }

  &:active {
    background-color: darken($subject-color, 5%);
    border-color: darken($subject-color, 5%);
  }
}

.el-link--primary {
  color: $subject-color;

  &:hover {
    color: darken($subject-color, 10%);
  }
}

/* 页面过渡动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* 加载状态样式 */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;

  .loading-text {
    color: #666;
    font-size: 14px;
    margin-left: 8px;
  }
}

/* 错误状态样式 */
.error-container {
  text-align: center;
  padding: 40px 20px;

  .error-icon {
    font-size: 48px;
    color: #f56c6c;
    margin-bottom: 16px;
  }

  .error-title {
    font-size: 18px;
    color: #333;
    margin-bottom: 8px;
  }

  .error-message {
    font-size: 14px;
    color: #666;
    margin-bottom: 20px;
  }
}

/* 空状态样式 */
.empty-container {
  text-align: center;
  padding: 40px 20px;

  .empty-icon {
    font-size: 48px;
    color: #ddd;
    margin-bottom: 16px;
  }

  .empty-text {
    font-size: 16px;
    color: #999;
  }
}

/* 文本工具类 */
.text-center {
  text-align: center;
}
.text-left {
  text-align: left;
}
.text-right {
  text-align: right;
}

.text-primary {
  color: $subject-color;
}
.text-success {
  color: #67c23a;
}
.text-warning {
  color: #e6a23c;
}
.text-danger {
  color: #f56c6c;
}
.text-info {
  color: #909399;
}

/* 间距工具类 */
.mt-10 {
  margin-top: 10px;
}
.mt-20 {
  margin-top: 20px;
}
.mt-30 {
  margin-top: 30px;
}
.mb-10 {
  margin-bottom: 10px;
}
.mb-20 {
  margin-bottom: 20px;
}
.mb-30 {
  margin-bottom: 30px;
}

.pt-10 {
  padding-top: 10px;
}
.pt-20 {
  padding-top: 20px;
}
.pt-30 {
  padding-top: 30px;
}
.pb-10 {
  padding-bottom: 10px;
}
.pb-20 {
  padding-bottom: 20px;
}
.pb-30 {
  padding-bottom: 30px;
}

/* 打印样式 */
@media print {
  .no-print {
    display: none !important;
  }

  #app {
    background: #fff !important;
  }

  .main-content {
    margin-top: 0 !important;
    min-height: auto !important;
  }
}

/* 强制密码修改弹窗样式 */
.force-password-change-dialog {
  .el-dialog {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  }

  .el-dialog__header {
    background: #e74c3c;
    color: #fff;
    padding: 24px;
    text-align: center;

    .el-dialog__title {
      color: #fff;
      font-weight: 500;
      font-size: 18px;
    }
  }

  .el-dialog__body {
    padding: 32px 24px 24px;
    background: #fff;
  }

  .el-dialog__footer {
    background: #fff;
    padding: 0 24px 24px;
    border-top: none;
  }
}

.password-change-header {
  text-align: center;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  .el-icon-warning {
    color: #e74c3c;
    font-size: 20px;
    margin: 0;
  }

  .password-change-title {
    margin: 0;
    font-size: 14px;
    color: #666;
    line-height: 1.4;
  }

  &.user-id-notice {
    background: linear-gradient(
      135deg,
      rgba(231, 76, 60, 0.1) 0%,
      rgba(192, 57, 43, 0.05) 100%
    );
    border: 1px solid rgba(231, 76, 60, 0.2);
    border-radius: 8px;
    padding: 10px 16px;
    margin-bottom: 20px;

    .el-icon-warning {
      color: #e74c3c;
      font-size: 18px;
    }

    .password-change-title {
      color: #2c3e50;
      font-weight: 500;
      line-height: 1.6;
    }
  }
}

.password-change-form {
  .el-form-item {
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .el-form-item__label {
    color: #333;
    font-weight: 500;
    font-size: 14px;
  }

  .el-input__inner {
    height: 44px;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    transition: all 0.2s ease;
    font-size: 14px;

    &:focus {
      border-color: #e74c3c;
      box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.1);
    }

    &::placeholder {
      color: #999;
    }
  }

  .el-form-item.is-error .el-input__inner {
    border-color: #e74c3c;
  }

  .el-form-item__error {
    color: #e74c3c;
    font-size: 12px;
  }
}

.password-change-btn {
  width: 100%;
  height: 44px;
  background: #e74c3c !important;
  border: none !important;
  border-radius: 8px;
  color: #fff !important;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.2s ease;
  margin-top: 16px;

  &:hover {
    background: #d62c1a !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(231, 76, 60, 0.3);
  }

  &:active {
    transform: translateY(0);
    background: #c0392b !important;
  }

  &.is-loading {
    background: #e74c3c !important;
    cursor: not-allowed;
  }
}
</style>
